// Module-level recording-playback coordinator.
//
// ── Why a singleton ────────────────────────────────────
// A voice recording can be played from two surfaces at
// once: the conversation-row mini player (`VoiceCallMessage`)
// and the call slideout's "study" scrubber
// (`CallDetailPanel`). Before this store each surface owned
// its own `<audio>` element, which produced three bugs:
//   #6 opening the slideout after playing on the row
//      restarted from 0,
//   #7 playing call A then call B left both audios playing,
//   #8 the row's waveform wasn't seek-clickable.
//
// The fix is one process-wide coordinator that owns AT MOST
// ONE active recording and ONE shared `HTMLAudioElement`.
// Every surface reads its time/playing state from here and
// drives play/pause/seek against the same element, so the
// two UIs are always in sync. Mirrors the established
// `voximplant-client.ts` `useSyncExternalStore` singleton.
//
// ── Testability ────────────────────────────────────────
// The audio element is created lazily through an injected
// factory (default: a real `new Audio()`), guarded for
// SSR / jsdom (`typeof Audio !== "undefined"`). Tests pass
// a fake element implementing the slice of the media API the
// store touches, so the coordination logic — pause-others,
// same-id resume, seek, ended — is exercised without a real
// media stack.

import { useCallback, useSyncExternalStore } from "react"

export interface RecordingPlaybackState {
  // The recording currently loaded into the shared audio
  // element. null when nothing has played yet.
  voiceCallId: number | null
  src: string | null
  currentTime: number
  // The audio element's loaded duration, or 0 before
  // `loadedmetadata` fires.
  duration: number
  isPlaying: boolean
}

// The slice of HTMLAudioElement the coordinator drives. A
// real `<audio>` satisfies it; the test fake implements just
// these members.
export interface PlaybackAudioElement {
  src: string
  currentTime: number
  duration: number
  paused: boolean
  play(): Promise<void>
  pause(): void
  load(): void
  addEventListener(type: string, listener: () => void): void
  removeEventListener(type: string, listener: () => void): void
}

export type AudioFactory = () => PlaybackAudioElement | null

const INITIAL_STATE: RecordingPlaybackState = {
  voiceCallId: null,
  src: null,
  currentTime: 0,
  duration: 0,
  isPlaying: false,
}

// Default factory: a real `<audio>` element, or null in
// SSR / jsdom where `Audio` is undefined (the store then
// degrades to pure state, which is all the tests need).
const defaultAudioFactory: AudioFactory = () =>
  typeof Audio === "undefined" ? null : (new Audio() as PlaybackAudioElement)

export class RecordingPlaybackStore {
  private state: RecordingPlaybackState = INITIAL_STATE
  private listeners = new Set<() => void>()

  private audio: PlaybackAudioElement | null = null
  private readonly createAudio: AudioFactory

  constructor(createAudio: AudioFactory = defaultAudioFactory) {
    this.createAudio = createAudio
  }

  // ── External store interface (useSyncExternalStore) ──────

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getState = (): RecordingPlaybackState => this.state

  private setState(patch: Partial<RecordingPlaybackState>): void {
    this.state = { ...this.state, ...patch }
    this.listeners.forEach((l) => l())
  }

  // Lazily build the single shared audio element and wire its
  // media events into store state. Returns null in SSR / jsdom.
  private ensureAudio(): PlaybackAudioElement | null {
    if (this.audio) return this.audio
    const audio = this.createAudio()
    if (!audio) return null
    this.audio = audio
    audio.addEventListener("timeupdate", this.handleTimeUpdate)
    audio.addEventListener("loadedmetadata", this.handleLoadedMetadata)
    audio.addEventListener("ended", this.handleEnded)
    audio.addEventListener("play", this.handlePlay)
    audio.addEventListener("pause", this.handlePause)
    return audio
  }

  private handleTimeUpdate = (): void => {
    if (!this.audio) return
    this.setState({ currentTime: this.audio.currentTime })
  }

  private handleLoadedMetadata = (): void => {
    if (!this.audio) return
    const d = this.audio.duration
    if (Number.isFinite(d) && d > 0) this.setState({ duration: d })
  }

  private handleEnded = (): void => {
    if (this.audio) this.audio.currentTime = 0
    this.setState({ isPlaying: false, currentTime: 0 })
  }

  private handlePlay = (): void => {
    this.setState({ isPlaying: true })
  }

  private handlePause = (): void => {
    this.setState({ isPlaying: false })
  }

  // ── Public API ───────────────────────────────────────────

  // Play (or resume) the recording for `voiceCallId`.
  //   - A DIFFERENT recording active → pause + reset it first,
  //     load the new src, start at `fromSeconds ?? 0` (#7).
  //   - SAME recording → resume from the current position, or
  //     from `fromSeconds` when one is given (#6).
  play(voiceCallId: number, src: string, fromSeconds?: number): void {
    const audio = this.ensureAudio()
    const sameRecording = this.state.voiceCallId === voiceCallId

    if (!sameRecording) {
      // Pause + reset whatever was playing before swapping src,
      // so two recordings can never play at once.
      if (audio && !audio.paused) audio.pause()
      const start = fromSeconds ?? 0
      this.setState({
        voiceCallId,
        src,
        currentTime: start,
        duration: 0,
        isPlaying: false,
      })
      if (audio) {
        audio.src = src
        audio.load()
        audio.currentTime = start
      }
    } else if (fromSeconds != null) {
      this.setState({ currentTime: fromSeconds })
      if (audio) audio.currentTime = fromSeconds
    }

    if (audio) {
      void audio
        .play()
        .then(() => this.setState({ isPlaying: true }))
        .catch(() => {})
    } else {
      // No real media element (SSR / jsdom) — reflect intent in
      // state so the UI still toggles to "playing".
      this.setState({ isPlaying: true })
    }
  }

  // Pause without losing position.
  pause(): void {
    if (this.audio) this.audio.pause()
    this.setState({ isPlaying: false })
  }

  // Seek the active recording, preserving the playing state.
  seek(seconds: number): void {
    const clamped = Math.max(0, seconds)
    if (this.audio) this.audio.currentTime = clamped
    this.setState({ currentTime: clamped })
  }

  // Convenience: pause when this recording is the active one
  // and playing; otherwise (re)play it.
  togglePlay(voiceCallId: number, src: string): void {
    const active = this.state.voiceCallId === voiceCallId
    if (active && this.state.isPlaying) {
      this.pause()
    } else {
      this.play(voiceCallId, src)
    }
  }
}

// The process-wide singleton. `useRecordingPlayback` is the
// only consumer; everything else goes through the hook.
let singleton: RecordingPlaybackStore | null = null

export function getRecordingPlaybackStore(): RecordingPlaybackStore {
  if (!singleton) singleton = new RecordingPlaybackStore()
  return singleton
}

// Test seam: reset the module singleton between specs.
export function __resetRecordingPlaybackStoreForTests(
  next: RecordingPlaybackStore | null,
): void {
  singleton = next
}

export interface UseRecordingPlayback {
  // Whether THIS recording is the one loaded in the shared
  // element (vs. a sibling recording, or nothing).
  isActive: boolean
  isPlaying: boolean
  currentTime: number
  duration: number
  play: (fromSeconds?: number) => void
  pause: () => void
  seek: (seconds: number) => void
  toggle: () => void
}

// Subscribes a component to the playback singleton for one
// `voiceCallId` + `src`. Any number of components mount this
// for the same recording and render against the SAME audio
// element / time.
export function useRecordingPlayback(
  voiceCallId: number,
  src: string | null,
): UseRecordingPlayback {
  const store = getRecordingPlaybackStore()
  const state = useSyncExternalStore(
    store.subscribe,
    store.getState,
    store.getState,
  )

  const isActive = state.voiceCallId === voiceCallId

  const play = useCallback(
    (fromSeconds?: number) => {
      if (!src) return
      store.play(voiceCallId, src, fromSeconds)
    },
    [store, voiceCallId, src],
  )
  const pause = useCallback(() => store.pause(), [store])
  const seek = useCallback(
    (seconds: number) => {
      // Lazily load the recording into the element if a seek
      // arrives before play (e.g. clicking a transcript turn).
      if (!isActive && src) store.play(voiceCallId, src, seconds)
      else store.seek(seconds)
    },
    [store, isActive, src, voiceCallId],
  )
  const toggle = useCallback(() => {
    if (!src) return
    store.togglePlay(voiceCallId, src)
  }, [store, voiceCallId, src])

  return {
    isActive,
    isPlaying: isActive ? state.isPlaying : false,
    currentTime: isActive ? state.currentTime : 0,
    duration: isActive ? state.duration : 0,
    play,
    pause,
    seek,
    toggle,
  }
}
